import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Dices, Eye, EyeOff, KeyRound, X } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';

const PASSWORD_RULES = [
  { label: 'Të paktën 8 karaktere', test: (value) => value.length >= 8 },
  { label: 'Një shkronjë të madhe (A–Z)', test: (value) => /[A-Z]/.test(value) },
  { label: 'Një shkronjë të vogël (a–z)', test: (value) => /[a-z]/.test(value) },
  { label: 'Një numër (0–9)', test: (value) => /\d/.test(value) },
  { label: 'Një karakter special (!@#$...)', test: (value) => /[^A-Za-z0-9]/.test(value) },
];

const PasswordField = ({ id, label, value, onChange, show, onToggle, onGenerate, autoComplete }) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="text-xs font-semibold text-zinc-800">{label}</label>
    <div className="relative">
      <Input
        id={id}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        className={`h-10 rounded-xl border-zinc-200 bg-white text-sm focus-visible:border-[#DD0000] focus-visible:ring-[#FFCE00] ${onGenerate ? 'pr-[4.5rem]' : 'pr-10'}`}
      />
      {onGenerate && (
        <button
          type="button"
          onClick={onGenerate}
          className="absolute inset-y-0 right-10 flex w-8 items-center justify-center text-zinc-500 transition hover:text-[#DD0000] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFCE00]"
          aria-label="Gjenero fjalëkalim të sigurt"
          title="Gjenero fjalëkalim të sigurt"
        >
          <Dices size={16} />
        </button>
      )}
      <button
        type="button"
        onClick={onToggle}
        className="absolute inset-y-0 right-2 flex w-8 items-center justify-center text-zinc-500 hover:text-zinc-900"
        aria-label={show ? 'Fshih fjalëkalimin' : 'Shfaq fjalëkalimin'}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  </div>
);

const PasswordChangeDialog = ({ open, onClose, onSubmit }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [visibleFields, setVisibleFields] = useState({});
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  if (!open) return null;

  const rulesAreValid = PASSWORD_RULES.every((rule) => rule.test(newPassword));
  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!currentPassword) {
      setError('Shkruaj fjalëkalimin aktual.');
      return;
    }
    if (!rulesAreValid) {
      setError('Fjalëkalimi i ri nuk i plotëson kërkesat.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Fjalëkalimet e reja nuk përputhen.');
      return;
    }

    setIsSaving(true);
    try {
      const result = await onSubmit(currentPassword, newPassword);
      if (result === false || result?.success === false) {
        setError(result?.error || 'Fjalëkalimi nuk u ndryshua. Kontrollo fjalëkalimin aktual dhe provo përsëri.');
        return;
      }
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onClose();
    } catch (submitError) {
      console.error('Unable to change account password:', submitError);
      setError('Fjalëkalimi nuk u ndryshua. Provo përsëri.');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleVisibility = (field) => {
    setVisibleFields((previous) => ({ ...previous, [field]: !previous[field] }));
  };

  const closeDialog = () => {
    if (isSaving) return;
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setVisibleFields({});
    setError('');
    onClose();
  };

  const generatePassword = () => {
    const characterSets = [
      'ABCDEFGHJKLMNPQRSTUVWXYZ',
      'abcdefghijkmnopqrstuvwxyz',
      '23456789',
      '!@#$%&*+-=?',
    ];
    const allCharacters = characterSets.join('');
    const secureIndex = (length) => {
      const randomValues = new Uint8Array(1);
      const maxValidValue = 256 - (256 % length);
      do {
        window.crypto.getRandomValues(randomValues);
      } while (randomValues[0] >= maxValidValue);
      return randomValues[0] % length;
    };

    try {
      if (!window.crypto?.getRandomValues) {
        throw new Error('Secure random number generation is unavailable.');
      }
      const characters = characterSets.map((set) => set[secureIndex(set.length)]);
      while (characters.length < 16) {
        characters.push(allCharacters[secureIndex(allCharacters.length)]);
      }
      for (let index = characters.length - 1; index > 0; index -= 1) {
        const swapIndex = secureIndex(index + 1);
        [characters[index], characters[swapIndex]] = [characters[swapIndex], characters[index]];
      }
      const generatedPassword = characters.join('');
      setNewPassword(generatedPassword);
      setConfirmPassword(generatedPassword);
      setVisibleFields((previous) => ({ ...previous, next: true, confirm: true }));
      setError('');
    } catch (generationError) {
      console.error('Unable to generate a secure password:', generationError);
      setError('Nuk u gjenerua fjalëkalimi i sigurt. Provo përsëri.');
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1100] flex min-h-screen min-w-full items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="password-dialog-title"
        className="w-full max-w-lg overflow-hidden rounded-2xl border-0 bg-white text-zinc-950 shadow-2xl"
      >
        <header className="flex items-start justify-between border-b border-zinc-100 px-5 py-4">
          <div>
            <h2 id="password-dialog-title" className="text-lg font-semibold">Ndrysho fjalëkalimin</h2>
            <p className="mt-1 text-sm text-zinc-500">Shkruani fjalëkalimin aktual, pastaj zgjidhni një të ri.</p>
          </div>
          <button
            type="button"
            onClick={closeDialog}
            className="rounded-lg p-1.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            aria-label="Mbyll"
          >
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 px-5 py-5">
            <PasswordField
              id="current-password"
              label="Fjalëkalimi aktual"
              value={currentPassword}
              onChange={setCurrentPassword}
              show={Boolean(visibleFields.current)}
              onToggle={() => toggleVisibility('current')}
              autoComplete="current-password"
            />
            <PasswordField
              id="new-password"
              label="Fjalëkalimi i ri"
              value={newPassword}
              onChange={setNewPassword}
              show={Boolean(visibleFields.next)}
              onToggle={() => toggleVisibility('next')}
              onGenerate={generatePassword}
              autoComplete="new-password"
            />

            <ul className="space-y-1.5" aria-label="Kërkesat për fjalëkalimin">
              {PASSWORD_RULES.map(({ label, test }) => {
                const valid = test(newPassword);
                return (
                  <li key={label} className={`flex items-center gap-2 text-xs ${valid ? 'text-emerald-700' : 'text-zinc-500'}`}>
                    {valid ? <Check size={14} /> : <X size={14} />}
                    {label}
                  </li>
                );
              })}
            </ul>

            <PasswordField
              id="confirm-password"
              label="Konfirmo fjalëkalimin"
              value={confirmPassword}
              onChange={setConfirmPassword}
              show={Boolean(visibleFields.confirm)}
              onToggle={() => toggleVisibility('confirm')}
              autoComplete="new-password"
            />
            {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
          </div>

          <footer className="flex justify-end gap-2 border-t border-zinc-100 bg-zinc-50 px-5 py-3">
            <Button type="button" variant="outline" size="sm"             onClick={closeDialog} disabled={isSaving}>
              Anulo
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={isSaving || !currentPassword || !rulesAreValid || !confirmPassword}
              className="bg-zinc-950 text-[#FFCE00] hover:bg-[#DD0000] hover:text-white"
            >
              <KeyRound size={14} />
              {isSaving ? 'Duke ndryshuar...' : 'Ndrysho fjalëkalimin'}
            </Button>
          </footer>
        </form>
      </section>
    </div>,
    document.body
  );
};

export default PasswordChangeDialog;
